import { Link } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Shield, Diamond, Star, Globe, CaretDown, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";

const fleetImages = [
  { src: "https://customer-assets.emergentagent.com/job_parisgo/artifacts/stw5s5ky_703D55C7-6491-4A1D-BBD1-3724BB576F38.png", alt: "Mercedes Classe E — ALTURA", label: "Mercedes Classe E" },
  { src: "https://customer-assets.emergentagent.com/job_parisgo/artifacts/8up45an8_8CF1CAA1-D304-4DC3-99AE-C4B9AE562F84.jpeg", alt: "Mercedes Classe S — ALTURA", label: "Mercedes Classe S" },
  { src: "https://customer-assets.emergentagent.com/job_parisgo/artifacts/bat48e3f_E8F9F042-84D5-4B63-87E0-68156E0CD47E.png", alt: "Mercedes Classe V — ALTURA", label: "Mercedes Classe V" },
  { src: "https://customer-assets.emergentagent.com/job_parisgo/artifacts/nvf2ktbp_A218CC23-6605-4A15-A797-D5667A10ABE1.png", alt: "Mercedes Classe G — ALTURA", label: "Mercedes Classe G" },
  { src: "https://customer-assets.emergentagent.com/job_parisgo/artifacts/g4j06n8c_EA53DE21-6D52-41A3-8649-CA99B77FA055.png", alt: "Range Rover Autobiography — ALTURA", label: "Range Rover" },
  { src: "https://customer-assets.emergentagent.com/job_parisgo/artifacts/f97xw5lg_994950FD-BC1F-48CB-AEDC-7B53591C370A.png", alt: "Mercedes-Maybach Classe S — ALTURA", label: "Maybach" },
];

const FleetCarousel = () => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);

  const goTo = useCallback((index) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  }, [current]);

  const next = useCallback(() => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % fleetImages.length);
  }, []);

  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + fleetImages.length) % fleetImages.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <div className="relative overflow-hidden rounded-none" data-testid="fleet-carousel">
      <div className="relative h-[400px] sm:h-[450px]">
        <AnimatePresence initial={false} custom={direction}>
          <motion.img
            key={current}
            src={fleetImages[current].src}
            alt={fleetImages[current].alt}
            custom={direction}
            initial={{ opacity: 0, x: direction * 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -100 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        {/* Vehicle label */}
        <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-4 py-2">
          <p className="text-[#1A1A1A] text-sm font-medium tracking-wide">{fleetImages[current].label}</p>
        </div>
      </div>
      {/* Navigation arrows */}
      <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors" data-testid="carousel-prev">
        <CaretLeft size={18} weight="bold" className="text-[#1A1A1A]" />
      </button>
      <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors" data-testid="carousel-next">
        <CaretRight size={18} weight="bold" className="text-[#1A1A1A]" />
      </button>
      {/* Dots */}
      <div className="absolute bottom-4 right-4 flex gap-2">
        {fleetImages.map((_, i) => (
          <button key={`dot-${i}`} onClick={() => goTo(i)} className={`w-2 h-2 rounded-full transition-all ${i === current ? "bg-[#1A1A1A] w-6" : "bg-[#1A1A1A]/30"}`} data-testid={`carousel-dot-${i}`} />
        ))}
      </div>
    </div>
  );
};

const FaqItem = ({ question, answer, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="bg-white border border-[#1A1A1A]/10"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left"
        data-testid={`faq-toggle-${index}`}
      >
        <span className="text-[#1A1A1A] text-sm sm:text-base font-medium pr-4">{question}</span>
        <CaretDown size={18} weight="bold" className={`text-[#1A1A1A]/50 flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-5 text-[#1A1A1A]/70 text-sm font-light leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const HomePage = () => {
  const { t } = useTranslation();

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const services = [
    { title: t("home.service_1_title"), description: t("home.service_1_desc"), path: "/services/deplacements", icon: Diamond },
    { title: t("home.service_2_title"), description: t("home.service_2_desc"), path: "/services/greet-meet", icon: Star },
    { title: t("home.service_3_title"), description: t("home.service_3_desc"), path: "/services/securite", icon: Shield },
    { title: t("home.service_4_title"), description: t("home.service_4_desc"), path: "/services/transport-luxe", icon: Globe },
  ];

  return (
    <main data-testid="home-page" className="bg-[#EDEBE4]">
      <SEO path="/" description="ALTURA Services : transport privé de luxe et nettoyage professionnel à Paris et en Île-de-France. Conciergerie, chauffeurs, transferts, nettoyage automobile, mobilier et appartement." />

      {/* Division Chooser Hero */}
      <section className="relative flex flex-col" data-testid="division-hero">
        {/* ALTURA Header — hidden on mobile, shown on desktop over the split */}
        <div className="hidden md:block absolute top-0 left-0 right-0 z-20 text-center pt-16">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-heading text-6xl lg:text-7xl tracking-[0.25em] uppercase text-white mix-blend-difference pointer-events-none"
          >
            Altura
          </motion.h1>
        </div>

        {/* Two Divisions */}
        <div className="grid md:grid-cols-2 md:min-h-screen">
          {/* LEFT — Conciergerie */}
          <Link to="/services" className="relative group overflow-hidden flex items-end min-h-[50vh] md:min-h-0" data-testid="choose-conciergerie">
            <div className="absolute inset-0">
              <img
                src="https://customer-assets.emergentagent.com/job_parisgo/artifacts/09akaoz4_4DC035C4-C368-4BCB-A49F-408B01512975.jpeg"
                alt="ALTURA Conciergerie — Transport privé Paris"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10 group-hover:from-black/90 transition-colors duration-500" />
            </div>
            <div className="relative z-10 p-6 sm:p-12 lg:p-16 w-full">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <p className="text-white/40 text-[10px] sm:text-xs tracking-[0.3em] uppercase mb-2 sm:mb-3">Division</p>
                <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-white tracking-wide mb-2 sm:mb-3">
                  Conciergerie
                </h2>
                <p className="text-white/60 text-xs sm:text-base max-w-md leading-relaxed mb-4 sm:mb-6">
                  Transport privé, transferts aéroports, sécurité rapprochée et expériences sur mesure à Paris.
                </p>
                <span className="inline-flex items-center gap-2 text-white text-xs tracking-[0.15em] uppercase opacity-60 group-hover:opacity-100 group-hover:gap-3 transition-all">
                  Découvrir nos services <ArrowRight size={14} weight="bold" />
                </span>
              </motion.div>
            </div>
          </Link>

          {/* RIGHT — Property Services */}
          <Link to="/property-services" className="relative group overflow-hidden flex items-end min-h-[50vh] md:min-h-0" data-testid="choose-property">
            <div className="absolute inset-0">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/545976fc-774f-4132-bae0-c645581525da/images/1cb48aba6d5c12e00e98fccc98dcf0ab41a99ad9a0b742f9d12f32551cc4afcf.jpeg"
                alt="ALTURA Property Services — Nettoyage professionnel"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10 group-hover:from-black/90 transition-colors duration-500" />
            </div>
            <div className="relative z-10 p-6 sm:p-12 lg:p-16 w-full">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
                <p className="text-white/30 text-[10px] sm:text-xs tracking-[0.3em] uppercase mb-2 sm:mb-3">Division</p>
                <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-white tracking-wide mb-2 sm:mb-3">
                  Property Services
                </h2>
                <p className="text-white/50 text-xs sm:text-base max-w-md leading-relaxed mb-4 sm:mb-6">
                  Nettoyage automobile, mobilier, appartement et locaux commerciaux — service professionnel à domicile.
                </p>
                <span className="inline-flex items-center gap-2 text-white text-xs tracking-[0.15em] uppercase opacity-50 group-hover:opacity-100 group-hover:gap-3 transition-all">
                  Découvrir nos services <ArrowRight size={14} weight="bold" />
                </span>
              </motion.div>
            </div>
          </Link>
        </div>

        {/* Scroll indicator — desktop only */}
        <motion.div
          className="hidden md:block absolute bottom-6 left-1/2 -translate-x-1/2 z-20"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-px h-12 bg-gradient-to-b from-white/0 via-white/30 to-white/0" />
        </motion.div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 sm:py-32 bg-[#EDEBE4] relative">
        <div className="texture-overlay" />
        <div className="max-w-7xl mx-auto px-6 sm:px-12 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-6">{t("home.philosophy_subtitle")}</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1A1A1A] leading-tight mb-8">
                {t("home.philosophy_title_1")}<br /><span className="italic">{t("home.philosophy_title_2")}</span>
              </h2>
              <p className="text-[#1A1A1A]/80 leading-relaxed mb-6 font-light">
                {t("home.philosophy_p1")}
              </p>
              <p className="text-[#1A1A1A]/80 leading-relaxed font-light">
                {t("home.philosophy_p2")}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="grid grid-cols-2 gap-6"
            >
              <div className="bg-white border border-[#1A1A1A]/10 p-8 text-center">
                <p className="font-heading text-4xl text-[#1A1A1A] mb-2">{t("home.philosophy_stat1_value")}</p>
                <p className="text-[#1A1A1A]/70 text-sm tracking-wide">{t("home.philosophy_stat1_label")}</p>
              </div>
              <div className="bg-white border border-[#1A1A1A]/10 p-8 text-center">
                <p className="font-heading text-4xl text-[#1A1A1A] mb-2">Paris</p>
                <p className="text-[#1A1A1A]/70 text-sm tracking-wide">Worldwide</p>
              </div>
              <div className="bg-white border border-[#1A1A1A]/10 p-8 text-center">
                <p className="font-heading text-4xl text-[#1A1A1A] mb-2">{t("home.philosophy_stat2_value")}</p>
                <p className="text-[#1A1A1A]/70 text-sm tracking-wide">{t("home.philosophy_stat2_label")}</p>
              </div>
              <div className="bg-white border border-[#1A1A1A]/10 p-8 text-center">
                <p className="font-heading text-4xl text-[#1A1A1A] mb-2">VIP</p>
                <p className="text-[#1A1A1A]/70 text-sm tracking-wide">Service exclusif</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Video Experience Section */}
      <section className="relative h-[60vh] min-h-[400px] overflow-hidden bg-[#1A1A1A]" data-testid="home-video-section">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-70"
          poster="https://customer-assets.emergentagent.com/job_parisgo/artifacts/8up45an8_8CF1CAA1-D304-4DC3-99AE-C4B9AE562F84.jpeg"
        >
          <source src="https://customer-assets.emergentagent.com/job_parisgo/artifacts/iyg0ykou_9DE32516-F601-4A26-833B-EE58342C0D26.mov" type="video/mp4" />
        </video>
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center px-6"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-[#EDEBE4]/60 mb-4">{t("home.video_subtitle")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#EDEBE4] mb-6">
              {t("home.video_title")}
            </h2>
            <Link to="/services/deplacements" className="bg-[#EDEBE4] text-[#1A1A1A] px-10 py-3 text-sm tracking-wider uppercase hover:bg-white transition-colors inline-flex items-center gap-2" data-testid="video-cta">
              {t("home.hero_cta")} <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 sm:py-32 bg-[#E5E3DC]" data-testid="home-services-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-4">{t("home.services_subtitle")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1A1A1A]">
              {t("home.services_title_1")} <span className="italic">{t("home.services_title_2")}</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link to={service.path} className="service-card block bg-white p-8 lg:p-12 group" data-testid={`home-service-${index}`}>
                  <service.icon size={32} weight="thin" className="text-[#1A1A1A] mb-6" />
                  <h3 className="font-heading text-xl lg:text-2xl text-[#1A1A1A] mb-4 group-hover:text-[#1A1A1A]/70 transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-[#1A1A1A]/70 text-sm font-light mb-6">{service.description}</p>
                  <span className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#1A1A1A]/50 group-hover:text-[#1A1A1A] transition-colors duration-300">
                    {t("home.learn_more")}
                    <ArrowRight size={14} weight="bold" className="transform group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet Preview */}
      <section className="py-24 sm:py-32 bg-[#EDEBE4] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="order-2 lg:order-1">
              <FleetCarousel />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="order-1 lg:order-2">
              <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-6">{t("home.fleet_subtitle")}</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1A1A1A] leading-tight mb-8">
                {t("home.fleet_title_1")}<br /><span className="italic">{t("home.fleet_title_2")}</span>
              </h2>
              <ul className="space-y-4 mb-8">
                {[
                  { name: "Mercedes Classe E", link: "/services/deplacements", capacity: 3 },
                  { name: "Mercedes Classe S", link: "/services/deplacements", capacity: 3 },
                  { name: "Mercedes Classe V", link: "/services/deplacements", capacity: 7 },
                  { name: "Range Rover Autobiography", link: "/vehicules/range-rover", capacity: 3 },
                  { name: "Mercedes Classe G", link: "/vehicules/classe-g", capacity: 3 },
                  { name: "Mercedes-Maybach Classe S", link: "/vehicules/maybach", capacity: 2 },
                ].map((vehicle) => (
                  <li key={vehicle.name}>
                    <Link to={vehicle.link} className="flex items-center gap-4 text-[#1A1A1A]/80 hover:text-[#1A1A1A] transition-colors group">
                      <span className="w-2 h-px bg-[#1A1A1A] group-hover:w-4 transition-all" />
                      <span className="flex-1">{vehicle.name}</span>
                      {vehicle.capacity && <span className="text-[#1A1A1A]/40 text-xs tracking-wide">{vehicle.capacity} pass. max</span>}
                    </Link>
                  </li>
                ))}
              </ul>
              <Link to="/services/deplacements" className="btn-primary inline-flex items-center gap-3" data-testid="home-fleet-cta">
                {t("home.fleet_cta")}
                <ArrowRight size={16} weight="bold" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Destinations populaires — SEO internal links */}
      <section className="py-24 sm:py-32 bg-white relative">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-4">{t("home.destinations_subtitle")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1A1A1A]">
              {t("home.destinations_title")}
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: "Aéroport CDG", desc: "Transfert Paris ↔ Roissy", price: "dès 120€", link: "/transfert-aeroport-cdg" },
              { name: "Aéroport Orly", desc: "Transfert Paris ↔ Orly", price: "dès 120€", link: "/transfert-aeroport-orly" },
              { name: "Disneyland Paris", desc: "Transfert direct aux parcs", price: "dès 110€", link: "/chauffeur-prive-disneyland" },
              { name: "Château de Versailles", desc: "Visite royale avec chauffeur", price: "dès 100€", link: "/chauffeur-prive-versailles" },
            ].map((dest, i) => (
              <motion.div key={dest.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Link to={dest.link} className="block bg-[#EDEBE4] border border-[#1A1A1A]/10 p-6 hover:border-[#1A1A1A]/30 transition-all group" data-testid={`destination-link-${i}`}>
                  <h3 className="font-heading text-lg text-[#1A1A1A] mb-1">{dest.name}</h3>
                  <p className="text-[#1A1A1A]/60 text-sm mb-3">{dest.desc}</p>
                  <p className="text-[#1A1A1A] text-sm font-medium">{dest.price}</p>
                  <ArrowRight size={16} className="text-[#1A1A1A]/30 group-hover:text-[#1A1A1A] group-hover:translate-x-1 transition-all mt-3" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section — SEO rich content */}
      <section className="py-24 sm:py-32 bg-[#EDEBE4] relative" data-testid="home-faq-section">
        <div className="texture-overlay" />
        <div className="max-w-4xl mx-auto px-6 sm:px-12 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-4">{t("home.faq_subtitle")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1A1A1A]">
              {t("home.faq_title")}
            </h2>
          </motion.div>
          <div className="space-y-3">
            {[
              { q: t("home.faq_1_q"), a: t("home.faq_1_a") },
              { q: t("home.faq_2_q"), a: t("home.faq_2_a") },
              { q: t("home.faq_3_q"), a: t("home.faq_3_a") },
              { q: t("home.faq_4_q"), a: t("home.faq_4_a") },
              { q: t("home.faq_5_q"), a: t("home.faq_5_a") },
            ].map((faq, i) => (
              <FaqItem key={faq.q} question={faq.q} answer={faq.a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-[#E5E3DC] relative">
        <div className="texture-overlay" />
        <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-6">{t("home.cta_subtitle")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1A1A1A] mb-8">
              {t("home.cta_title_1")} <span className="italic">{t("home.cta_title_2")}</span>
            </h2>
            <p className="text-[#1A1A1A]/80 font-light max-w-2xl mx-auto mb-12">{t("home.cta_desc")}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/services/deplacements" className="btn-dark inline-flex items-center gap-3 justify-center" data-testid="home-cta-book">
                {t("home.cta_book")}
                <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/contact" className="btn-primary inline-flex items-center gap-3 justify-center" data-testid="home-cta-contact">
                {t("home.cta_contact")}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
